import { useEffect, useRef } from "react";
import "./About.css";

export default function About() {
  const sectionRef = useRef(null);

  const points = [
    { t: "Performance-first marketing", d: "We focus on results — leads, sales, and growth." },
    { t: "Content + Ads together", d: "Short-forms, posters, and campaigns that match your brand." },
    { t: "Clear reporting", d: "Weekly updates so you always know what’s happening." },
    { t: "Fast execution", d: "Simple process to launch quickly and improve consistently." },
  ];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    // mount + in-view reveal
    const raf = requestAnimationFrame(() => el.classList.add("is-mounted"));

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("is-visible");
          io.disconnect();
        }
      },
      { threshold: 0.22 }
    );
    io.observe(el);

    // motion safety
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) {
      return () => {
        cancelAnimationFrame(raf);
        io.disconnect();
      };
    }

    // subtle parallax for bg
    const setParallax = (x, y) => {
      el.style.setProperty("--mx", String(x));
      el.style.setProperty("--my", String(y));
    };
    setParallax(0.5, 0.5);

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      const cx = Math.max(0, Math.min(1, x));
      const cy = Math.max(0, Math.min(1, y));
      setParallax(cx.toFixed(4), cy.toFixed(4));
    };

    const onLeave = () => setParallax(0.5, 0.5);

    el.addEventListener("pointermove", onMove, { passive: true });
    el.addEventListener("pointerleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <section id="about" className="about" ref={sectionRef}>
      {/* decorative layer */}
      <div className="about__bg" aria-hidden="true">
        <span className="about__orb about__orb--1" />
        <span className="about__orb about__orb--2" />
        <span className="about__grid" />
      </div>

      <div className="container">
        <span className="about__kicker reveal" style={{ "--d": "0ms" }}>
          About
        </span>

        <div className="about__gridWrap">
          <div className="about__left">
            <h2 className="about__title reveal" style={{ "--d": "90ms" }}>
              We help brands grow with smart digital marketing.
            </h2>

            <p className="about__text reveal" style={{ "--d": "180ms" }}>
              NesflixDigital is a digital marketing agency that helps businesses build their brand,
              connect with the right audience, and drive sales through strategy, content, and ads.
              We work on platforms like Facebook, Instagram, TikTok, and Google with a simple goal:
              real growth with clear communication.
            </p>

            <div className="about__ctaRow reveal" style={{ "--d": "270ms" }}>
              <a className="about__cta about__cta--primary" href="#contact">
                Get Free Strategy Call
              </a>
              <a className="about__cta" href="#work">
                See Our Work
              </a>
            </div>
          </div>

          <div className="about__right">
            {points.map((p, i) => (
              <div
                className="about__card reveal"
                style={{ "--d": `${300 + i * 90}ms` }}
                key={p.t}
              >
                <div className="about__dot" />
                <div>
                  <div className="about__cardTitle">{p.t}</div>
                  <div className="about__cardDesc">{p.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
